import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  SimpleGrid,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useBillItems } from "../context/tableItemsContext";
import io from "socket.io-client";
import { useTableTime } from "../context/tableTimeContext";
import { useKotContext } from "../context/kotContext";
import SubTableUpdateModal from "./subTableUpdateModal";
import { MdOutlineSubject } from "react-icons/md";
import { compareTableNames } from "./tableSortUtils";

const socket = io("https://api.rcb.tdrsoftware.in");

const TableStatus = () => {
  const { elapsedTime } = useTableTime();
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { holdTimes, fetchKotNo } = useKotContext();
  const [subTableNo, setSubTableNo] = useState(null);

  const customerId = localStorage.getItem("customerId");
  const recentQuickBills =
    JSON.parse(localStorage.getItem("recentQuickBills")) || [];

  const [order, setOrder] = useState({});
  const [fields, setFields] = useState({});
  const { billItems, fetchTableBillItems } = useBillItems();
  const [data, setData] = useState([]);
  const [subTable, setSubTable] = useState(null);
  // ⬆️ inside component state
  const [refreshedTables, setRefreshedTables] = useState([]);

  const [filter, setFilter] = useState({
    ac: "",
    status: "",
  });

  const [searchTable, setSearchTable] = useState("");

  const navigate = useNavigate();
  const kotSaved = JSON.parse(localStorage.getItem("kotPrints")) || [];

  const handleOpenModal = (tableNo, sub) => {
    setSubTableNo(tableNo);
    setSubTable(sub);
    onOpen();
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `https://api.rcb.tdrsoftware.in/api/getTableData/${customerId}`
      );
      setData(response.data.table);

      response.data.table.forEach((table) => {
        fetchTableData(table.tableNo);
        fetchKotNo(customerId, table.tableNo);
      });
    } catch (error) {
      console.error("Error fetching table data:", error);
    }
  };

  const fetchTableData = async (tableNo) => {
    try {
      const response = await axios.get(
        `https://api.rcb.tdrsoftware.in/api/getItems/${customerId}/${tableNo}`
      );
      setOrder((prev) => ({
        ...prev,
        [tableNo]: response.data.items,
      }));
      setFields((prev) => ({
        ...prev,
        [tableNo]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching table items:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [customerId]);

  useEffect(() => {
    const customerId = localStorage.getItem("customerId");
    socket.emit("joinRoom", customerId);

    const refreshSwitchedTables = ({ fromTable, toTable }) => {
      if (!fromTable || !toTable) return;
      fetchTableData(fromTable);
      fetchTableData(toTable);
    };
    socket.on("tableSwitched", refreshSwitchedTables);

    socket.on("kotGenerated", (data) => {
      fetchTableData(data.tableNo);
      fetchTableBillItems(customerId, data.tableNo);
    });

    return () => {
      socket.off("tableSwitched", refreshSwitchedTables);
      socket.off("kotGenerated");
    };
  }, []);

  const getElapsedMinutes = (createdAt) => {
    const createdAtTime = new Date(createdAt);
    const now = new Date();
    const differenceInMinutes = Math.floor((now - createdAtTime) / (1000 * 60));
    return `${differenceInMinutes} mins`;
  };

  let filteredData = data.filter((item) => {
    return (
      (filter.ac === "" || item.ac === filter.ac) &&
      (filter.status === "" || item.status === filter.status)
    );
  });

  if (searchTable.trim() !== "") {
    filteredData = filteredData.filter((item) =>
      item.tableNo.toLowerCase().includes(searchTable.toLowerCase())
    );
  }

  filteredData.sort((a, b) => compareTableNames(a.tableNo, b.tableNo));

  return (
    <Box p={{ base: 3, md: 5 }} width="100%" minWidth={0}>
      <Text as="h1" fontSize="xl" fontWeight="bold" lineHeight="1.3" mb={4}>
        Table Status
      </Text>
      {/* 🔍 Search + Filters */}
      <Box
        mb={4}
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
      >
        <Input
          placeholder="Search Table..."
          aria-label="Search tables"
          value={searchTable}
          onChange={(e) => setSearchTable(e.target.value)}
          width={{ base: "100%", sm: "260px" }}
          size="md"
        />

        <Button
          fontSize="sm"
          colorScheme={filter.ac === "" ? "blue" : "gray"}
          onClick={() => setFilter({ ac: "", status: "" })}
        >
          All
        </Button>
        <Button
          fontSize="sm"
          colorScheme={filter.ac === "Ac" ? "blue" : "gray"}
          onClick={() => setFilter({ ...filter, ac: "Ac" })}
        >
          Ac
        </Button>
        <Button
          fontSize="sm"
          colorScheme={filter.ac === "Non-Ac" ? "blue" : "gray"}
          onClick={() => setFilter({ ...filter, ac: "Non-Ac" })}
        >
          Non-Ac
        </Button>
      </Box>

      <SimpleGrid
        data-testid="table-status-grid"
        templateColumns="repeat(auto-fill, minmax(min(100%, 128px), 1fr))"
        alignItems="stretch"
        autoRows="minmax(108px, auto)"
        mb={3}
        gap={3}
        boxShadow="sm"
        p={3}
        borderRadius="6px"
        width="100%"
        minWidth={0}
      >
        {filteredData.map((table) => {
          const tableServer = kotSaved.find(
            (item) => item.tableNo === table.tableNo
          )?.serverName;
          const tableOrder = order[table.tableNo] || [];
          const tableFields = fields[table.tableNo] || {};
          const total = tableFields.total || 0;
          const pax = tableFields.pax;
          const createdAt = holdTimes[table.tableNo];

          // ✅ check from context
          const hasBill = billItems.some(
            (bill) => bill.tableNo === table.tableNo && bill.billNo
          );

          // ✅ check from localStorage
          const storedBills =
            JSON.parse(localStorage.getItem("billItems")) || [];
          const hasDiscount = storedBills.some(
            (bill) =>
              bill.tableNo === table.tableNo &&
              bill.discountPercentage !== null &&
              bill.discountPercentage !== "" &&
              bill.discountPercentage !== undefined
          );

          // ✅ combined condition
          const showPrint = hasBill || hasDiscount;

          const elapsedTimeLabel = createdAt
            ? getElapsedMinutes(createdAt)
            : "";

          const tooltipLabel =
            tableOrder.length > 0
              ? `${tableOrder
                  .map((item) => `${item.name} x${item.quantity}`)
                  .join(", ")}` +
                ` | Total: ₹${total}` +
                (createdAt ? ` | Time: ${elapsedTimeLabel}` : "") +
                (pax !== undefined ? ` | Pax: ${pax}` : "") +
                (tableServer ? ` | Server: ${tableServer}` : "")
              : `No items ordered${
                  tableServer ? ` | Server: ${tableServer}` : ""
                }`;

          return (
            <Tooltip key={table.tableNo} label={tooltipLabel}>
              <Box position="relative" width="100%" minWidth={0} height="100%">
                {/* Table Box */}
                <Box
                  border={!table.status ? "1px dotted green" : "1px dotted red"}
                  bg={
                    refreshedTables.includes(table.tableNo)
                      ? "green"
                      : hasBill
                      ? "yellow"
                      : tooltipLabel === "No items ordered"
                      ? "green"
                      : "yellow"
                  }
                  borderRadius="8px"
                  role="button"
                  tabIndex={0}
                  aria-label={`Open table ${table.tableNo}`}
                  pt={9}
                  pb={4}
                  px={3}
                  width="100%"
                  minW={0}
                  minH="108px"
                  height="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  cursor="pointer"
                  color={
                    refreshedTables.includes(table.tableNo)
                      ? "white"
                      : tooltipLabel === "No items ordered"
                      ? "white"
                      : "black"
                  }
                  position="relative"
                  _focusVisible={{ outline: "3px solid", outlineColor: "blue.500", outlineOffset: "2px" }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      navigate("/restaurant", {
                        state: { tableNo: table.tableNo, ac: table.ac },
                      });
                    }
                  }}
                  onClick={() =>
                    navigate("/restaurant", {
                      state: { tableNo: table.tableNo, ac: table.ac },
                    })
                  }
                >
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    lineHeight="1.35"
                    whiteSpace="normal"
                    overflowWrap="anywhere"
                    maxWidth="100%"
                  >{table.tableNo}</Text>
                </Box>
                  {showPrint && (
                    <Button
                      aria-label={`Print table ${table.tableNo}`}
                      size="xs"
                      p={1}
                      position="absolute"
                      top="1"
                      left="1"
                      fontSize="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log(
                          "🖨 Printing Bill/Discount for",
                          table.tableNo
                        );
                      }}
                    >
                      🖨
                    </Button>
                  )}

                  {/* 📋 SubTable Button */}
                  {!table.tableNo.includes("-") && (
                    <Button
                      aria-label={`Subtables for ${table.tableNo}`}
                      size="xs"
                      p={1}
                      position="absolute"
                      top="1"
                      right="1"
                      fontSize="sm"
                      colorScheme="blue"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenModal(table.tableNo, table.subTable);
                      }}
                    >
                      <MdOutlineSubject />
                    </Button>
                  )}


                {/* <Button
  size="xs"
  fontSize="xs"
  colorScheme="green"
  position="absolute"
  bottom="-16px"
  right="-16px"
  borderRadius="full"
  minW="20px"
  h="20px"
  p={0}
  onClick={(e) => {
    e.stopPropagation();
    console.log("🔄 Refreshing & Clearing table", table.tableNo);

    // 1. Just clear local states for this table
    setOrder((prev) => ({ ...prev, [table.tableNo]: [] }));
    setFields((prev) => ({ ...prev, [table.tableNo]: {} }));

    // 2. Mark this table green
    setRefreshedTables((prev) => [...new Set([...prev, table.tableNo])]);

    // 3. Refresh bill status from server
    fetchTableData(table.tableNo);
    fetchTableBillItems(customerId, table.tableNo);
  }}
>
  ↻
</Button> */}
              </Box>
            </Tooltip>
          );
        })}
      </SimpleGrid>

      <SubTableUpdateModal
        customerId={customerId}
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        subTableNo={subTableNo}
        setSubTableNo={setSubTableNo}
        subTable={subTable}
        setSubTable={setSubTable}
      />
    </Box>
  );
};

export default TableStatus;
